import axios from 'axios';
import { SERVER_URL } from './config';

// Health API base URL - different from the main API_BASE
const HEALTH_API_BASE = `${SERVER_URL}/api/health`;

// Cache for circuit breaker data to prevent excessive API calls
let circuitBreakerCache = {
  data: null,
  timestamp: 0,
  ttl: 5000 // 5 seconds TTL
};

/**
 * Fetch all circuit breakers
 * @param {boolean} bypassCache - Whether to bypass the cache and force a fresh fetch
 * @returns Circuit breakers data
 */
export const fetchCircuitBreakers = async (bypassCache = false) => {
  try {
    // Check cache first if not bypassing
    const now = Date.now();
    if (!bypassCache && circuitBreakerCache.data && (now - circuitBreakerCache.timestamp < circuitBreakerCache.ttl)) {
      console.log('Using cached circuit breaker data');
      return circuitBreakerCache.data;
    }

    // Use the HEALTH_API_BASE with cache control headers
    const response = await axios.get(`${HEALTH_API_BASE}/circuit-breakers`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      // Add timeout to prevent long-running requests
      timeout: 10000
    });

    // Validate response data
    if (!response.data || typeof response.data !== 'object') {
      console.warn('Invalid circuit breaker response format:', response.data);
      return { circuitBreakers: [] };
    }

    // Ensure circuitBreakers is an array
    if (!Array.isArray(response.data.circuitBreakers)) {
      console.warn('Circuit breakers is not an array:', response.data.circuitBreakers);
      response.data.circuitBreakers = [];
    }

    // Update cache
    circuitBreakerCache.data = response.data;
    circuitBreakerCache.timestamp = now;

    return response.data;
  } catch (error) {
    console.error('Error fetching circuit breakers:', error);
    // Return a default structure to prevent UI errors
    return { circuitBreakers: [] };
  }
};

/**
 * Reset all circuit breakers
 * @returns Operation result
 */
export const resetAllCircuitBreakers = async () => {
  try {
    // Use the HEALTH_API_BASE
    const response = await axios.post(`${HEALTH_API_BASE}/circuit-breakers/reset`);
    return response.data;
  } catch (error) {
    console.error('Error resetting all circuit breakers:', error);
    throw error;
  }
};

/**
 * Reset a specific circuit breaker
 * @param name Circuit breaker name
 * @param type Circuit breaker type
 * @returns Operation result
 */
export const resetCircuitBreaker = async (name: string, type: string) => {
  try {
    // Use the HEALTH_API_BASE
    const response = await axios.post(`${HEALTH_API_BASE}/circuit-breakers/${name}/reset`, { type });
    return response.data;
  } catch (error) {
    console.error(`Error resetting circuit breaker ${name}:`, error);
    throw error;
  }
};

/**
 * Update circuit breaker configuration
 * @param name Circuit breaker name
 * @param type Circuit breaker type
 * @param config Configuration object with failureThreshold, resetTimeout, halfOpenSuccesses, autoResetTimeout
 * @returns Operation result
 */
export const updateCircuitBreakerConfig = async (
  name: string,
  type: string,
  config: {
    failureThreshold?: number;
    resetTimeout?: number;
    halfOpenSuccesses?: number;
    autoResetTimeout?: number;
  }
) => {
  try {
    // Use the HEALTH_API_BASE
    const response = await axios.post(
      `${HEALTH_API_BASE}/circuit-breakers/${name}/config?type=${type}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating circuit breaker ${name} configuration:`, error);
    throw error;
  }
};

/**
 * Fetch circuit breaker events
 * @param limit Maximum number of events to return
 * @returns Circuit breaker events
 */
export const fetchCircuitBreakerEvents = async (limit: number = 100) => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/circuit-breakers/events?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching circuit breaker events:', error);
    // Return a default structure to prevent UI errors
    return { events: [] };
  }
};

/**
 * Fetch events for a specific circuit breaker
 * @param name Circuit breaker name
 * @param limit Maximum number of events to return
 * @returns Circuit breaker events
 */
export const fetchCircuitBreakerEventsByName = async (name: string, limit: number = 100) => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/circuit-breakers/${name}/events?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching events for circuit breaker ${name}:`, error);
    // Return a default structure to prevent UI errors
    return { events: [] };
  }
};

/**
 * Fetch system health status
 * @returns System health data
 */
export const fetchHealthStatus = async () => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching health status:', error);
    // Return a default structure to prevent UI errors
    return { status: 'unknown', components: {} };
  }
};

/**
 * Run all health checks
 * @returns Health check results
 */
export const runHealthChecks = async () => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/check`);
    return response.data;
  } catch (error) {
    console.error('Error running health checks:', error);
    // Return a default structure to prevent UI errors
    return { status: 'unknown', checks: {} };
  }
};

/**
 * Run a specific health check
 * @param name Health check name
 * @returns Health check result
 */
export const runHealthCheck = async (name: string) => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/check/${name}`);
    return response.data;
  } catch (error) {
    console.error(`Error running health check ${name}:`, error);
    // Return a default structure to prevent UI errors
    return { status: 'unknown', details: {} };
  }
};

/**
 * Fetch system metrics
 * @returns System metrics data
 */
export const fetchSystemMetrics = async () => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/system`);
    return response.data;
  } catch (error) {
    console.error('Error fetching system metrics:', error);
    // Return a default structure to prevent UI errors
    return { cpu: {}, memory: {}, uptime: {} };
  }
};

/**
 * Fetch database metrics
 * @returns Database metrics data
 */
export const fetchDatabaseMetrics = async () => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/database`);
    return response.data;
  } catch (error) {
    console.error('Error fetching database metrics:', error);
    // Return a default structure to prevent UI errors
    return { connection: {}, collections: {} };
  }
};

/**
 * Fetch dashboard data
 * @returns Dashboard data
 */
export const fetchDashboardData = async () => {
  try {
    const response = await axios.get(`${HEALTH_API_BASE}/dashboard`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    // Return a default structure to prevent UI errors
    return {
      health: { status: 'unknown', components: {} },
      system: { cpu: {}, memory: {}, uptime: {} },
      database: { connection: {}, collections: {} },
      circuitBreakers: []
    };
  }
};
